import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";
import {
  Table,
  Button,
  Form,
  Input,
  message,
  Empty,
  Drawer,
  Spin,
  Dropdown,
  Modal,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
  ReloadOutlined,
  DeleteOutlined,
  MoreOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useSuppliersService, useSectionAccess } from "../../Services";
import { Supplier, SupplierAction } from "../../Types";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY } from "../../Constant";
import {
  PageHeader,
  tablePagination,
  DRAWER_FORM_WIDTH,
  drawerFooter,
  drawerTitle,
} from "../../components";

export const SuppliersPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [form] = Form.useForm();

  // Retrieve supplier queries and mutations from existing service hook
  const {
    suppliersQuery,
    createSupplierMutation,
    updateSupplierMutation,
    deleteSupplierMutation,
  } = useSuppliersService();

  const { data: suppliersResponse, isLoading, refetch } = suppliersQuery;
  // Handle case where API response wraps data or returns array directly
  const rawSuppliers: Supplier[] = Array.isArray(suppliersResponse)
    ? suppliersResponse
    : (suppliersResponse as any)?.data || [];

  // Filter suppliers on client-side
  const filteredSuppliers = rawSuppliers.filter((s: Supplier) => {
    if (!search) return true;
    const query = search.toLowerCase();
    return (
      s.name?.toLowerCase().includes(query) ||
      s.phone?.toLowerCase().includes(query) ||
      s.email?.toLowerCase().includes(query) ||
      s.address?.toLowerCase().includes(query)
    );
  });

  const total = filteredSuppliers.length;
  const { isAllowed } = useSectionAccess("suppliers");

  // Paginate on client-side
  const paginatedSuppliers = filteredSuppliers.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const openDrawer = (supplier?: Supplier) => {
    if (supplier) {
      setEditingSupplier(supplier);
      form.setFieldsValue({
        name: supplier.name,
        phone: supplier.phone || "",
        email: supplier.email || "",
        address: supplier.address || "",
      });
    } else {
      setEditingSupplier(null);
      form.resetFields();
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingSupplier(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const mutation = editingSupplier
        ? updateSupplierMutation
        : createSupplierMutation;

      const payload = editingSupplier
        ? { id: editingSupplier.id, ...values }
        : values;

      const res = await mutation.mutateAsync(payload);

      if (res?.success) {
        message.success(
          editingSupplier
            ? "Supplier updated successfully!"
            : "Supplier created successfully!",
        );
        closeDrawer();
      } else {
        if (res?.errors) {
          Object.entries(res.errors).forEach(([field, msg]) => {
            form.setFields([{ name: field, errors: [msg as string] }]);
          });
        } else {
          message.error(res?.message || "Operation failed.");
        }
      }
    } catch (error) {
      message.error("Please fill all the required fields.");
    }
  };

  const handleDelete = async (supplier: Supplier) => {
    try {
      const res = await deleteSupplierMutation.mutateAsync(supplier.id);
      if (res?.success) {
        message.success("Supplier deleted successfully!");
        if (paginatedSuppliers.length === 1 && page > 1) {
          setPage(page - 1);
        }
      } else {
        message.error(res?.message || "Failed to delete supplier.");
      }
    } catch (err: any) {
      message.error(err?.message || "Failed to delete supplier.");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setPage(1);
  };

  const columns = [
    {
      title: "Supplier",
      key: "name",
      render: (_: any, rec: Supplier) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <span className="text-gray-600 font-semibold text-xs">
              {rec.name[0]?.toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-medium text-gray-800 text-sm m-0 leading-tight">
              {rec.name}
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (v: string) =>
        v ? (
          <span className="text-sm text-gray-700">{v}</span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (v: string) =>
        v ? (
          <span className="text-sm text-gray-700">{v}</span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (v: string) =>
        v ? (
          <span className="text-sm text-gray-700">{v}</span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center" as const,
      render: (_: any, rec: Supplier) => {
        const menuItems = [
          ...(isAllowed(SupplierAction.Edit) ? [{
            key: "edit",
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => openDrawer(rec),
          }] : []),
          {
            key: "ledger",
            label: "View Ledger",
            icon: <EyeOutlined />,
            onClick: () => navigate(`/system/supplier_ledger?id=${rec.id}`),
          },
          ...(isAllowed(SupplierAction.Delete) ? [{
            key: "delete",
            label: "Delete",
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: "Confirm Deletion",
                content: "Are you sure you want to delete this supplier?",
                okText: "Yes",
                okType: "danger",
                cancelText: "No",
                onOk: () => handleDelete(rec),
              });
            },
          }] : []),
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              size="small"
              className="text-gray-400"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Supplier Management"
        subText={`Total: ${total} suppliers`}
        handleButtons={[
          ...(isAllowed(SupplierAction.Create) ? [{
            title: "Add Supplier",
            icon: PlusOutlined,
            onClick: () => {
              openDrawer();
            },
          }] : []),
          {
            title: "Refresh",
            icon: ReloadOutlined,
            onClick: () => refetch(),
          },
        ]}
        searchBar={
          <Input
            placeholder="Search by name, phone, email, address…"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={handleSearchChange}
            allowClear
            style={{ width: 300 }}
          />
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <Table
          dataSource={paginatedSuppliers}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={tablePagination({
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            itemName: "suppliers",
          })}
          scroll={{ x: 800 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No suppliers found"
              />
            ),
          }}
        />
      </div>

      <Drawer
        title={drawerTitle(
          editingSupplier ? "Edit Supplier" : "Add New Supplier",
        )}
        open={drawerOpen}
        onClose={closeDrawer}
        width={DRAWER_FORM_WIDTH}
        footer={drawerFooter({
          actions: [
            { label: "Cancel", onClick: closeDrawer },
            {
              label: editingSupplier ? "Save Changes" : "Create Supplier",
              type: "primary",
              loading:
                createSupplierMutation.isPending ||
                updateSupplierMutation.isPending,
              onClick: handleSubmit,
            },
          ],
        })}
      >
        <Spin
          spinning={
            createSupplierMutation.isPending || updateSupplierMutation.isPending
          }
        >
          <Form form={form} layout="vertical">
            <Form.Item
              label="Supplier Name"
              name="name"
              rules={[
                { required: true, message: "Please enter supplier name." },
              ]}
            >
              <Input placeholder="Enter supplier or company name" />
            </Form.Item>

            <Form.Item
              label="Phone Number"
              name="phone"
              rules={[
                {
                  pattern: /^[0-9+-\s()]*$/,
                  message: "Please enter a valid phone number.",
                },
              ]}
            >
              <Input placeholder="Enter phone number" />
            </Form.Item>

            <Form.Item
              label="Email Address (Optional)"
              name="email"
              rules={[
                {
                  type: "email",
                  message: "Please enter a valid email address.",
                },
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>

            <Form.Item label="Physical Address (Optional)" name="address">
              <Input.TextArea
                placeholder="Enter address details"
                rows={3}
                style={{ resize: "none" }}
              />
            </Form.Item>
          </Form>
        </Spin>
      </Drawer>
    </div>
  );
};
